import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { articleAPI, blockAPI, authAPI, workspaceAPI, templateAPI } from '../lib/api';
import { toast } from 'sonner';
import { RichTextEditor, BriefFieldEditor } from '../components/RichTextEditor';
import NotificationBell from '../components/NotificationBell';
import {
  ArrowLeft,
  Loader2,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Lock,
  Unlock,
  GripVertical,
  MessageSquare,
  Heading2,
  Type,
  List,
  Quote,
  Pencil,
  X,
  Check,
  FileDown,
  Eye,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const STATUS_CONFIG = {
  creating_brief: { label: 'Creating Brief', color: 'bg-slate-100 text-slate-700', phase: 'brief' },
  brief_ready: { label: 'Brief Ready', color: 'bg-blue-100 text-blue-700', phase: 'brief' },
  brief_under_review: { label: 'Brief Under Review', color: 'bg-indigo-100 text-indigo-700', phase: 'brief' },
  ready_to_write: { label: 'Ready to Write', color: 'bg-cyan-100 text-cyan-700', phase: 'brief' },
  writing_content: { label: 'Writing', color: 'bg-amber-100 text-amber-700', phase: 'writing' },
  on_hold: { label: 'On Hold', color: 'bg-gray-100 text-gray-700', phase: 'writing' },
  delayed: { label: 'Delayed', color: 'bg-red-100 text-red-700', phase: 'writing' },
  ready_for_review: { label: 'Ready for Review', color: 'bg-purple-100 text-purple-700', phase: 'review' },
  revision_requested: { label: 'Revision Requested', color: 'bg-orange-100 text-orange-700', phase: 'review' },
  editing: { label: 'Editing', color: 'bg-amber-100 text-amber-700', phase: 'writing' },
  finalized: { label: 'Finalized', color: 'bg-emerald-100 text-emerald-700', phase: 'review' },
};

const WORD_COUNT_MODES = [
  { value: 'up_to', label: 'Up to X Words' },
  { value: 'greater_than', label: 'Greater than X Words' },
  { value: 'less_than', label: 'Less than X Words' },
  { value: 'range', label: 'X to Y Words' },
];

const WRITER_EDITABLE_STATUSES = ["ready_to_write", "writing_content", "editing", "on_hold", "delayed", "revision_requested"];

export default function ArticleEditor() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [article, setArticle] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [writers, setWriters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  
  const [briefOpen, setBriefOpen] = useState(true);
  const [editingBrief, setEditingBrief] = useState(false);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [blockMenuPosition, setBlockMenuPosition] = useState(null);
  
  const [briefForm, setBriefForm] = useState({
    url_slug: '', primary_keyword: '', secondary_keywords: [],
    word_count_mode: null, objective: '', seo_instructions: '', writing_guidelines: ''
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [wordCountX, setWordCountX] = useState('');
  const [wordCountY, setWordCountY] = useState('');
  const [wordCountMode, setWordCountMode] = useState('');

  const hasPermission = (permission) => {
    if (userPermissions.includes('full_access') || userPermissions.includes('*')) return true;
    return userPermissions.includes(permission);
  };

  const canEditBrief = hasPermission('full_access') || hasPermission('edit_brief') || hasPermission('create_brief');
  const canEditContent = hasPermission('full_access') || hasPermission('edit_content');
  const isWriterEditable = WRITER_EDITABLE_STATUSES.includes(article?.status);
  const canAddBlocks = (canEditBrief && ['creating_brief', 'brief_ready'].includes(article?.status)) ||
                       (canEditContent && isWriterEditable);

  const loadData = useCallback(async () => {
    try {
      const [userRes, articleRes] = await Promise.all([
        authAPI.getMe(),
        articleAPI.get(articleId)
      ]);
      
      setUser(userRes.data);
      setArticle(articleRes.data);
      setUserPermissions(articleRes.data.user_permissions || []);
      
      const brief = articleRes.data.seo_brief || {};
      setBriefForm({
        url_slug: brief.url_slug || '',
        primary_keyword: brief.primary_keyword || '',
        secondary_keywords: brief.secondary_keywords || [],
        word_count_mode: brief.word_count_mode,
        objective: brief.objective || '',
        seo_instructions: brief.seo_instructions || '',
        writing_guidelines: brief.writing_guidelines || ''
      });
      
      if (brief.word_count_mode) {
        setWordCountMode(brief.word_count_mode.mode);
        setWordCountX(brief.word_count_mode.value_x?.toString() || '');
        setWordCountY(brief.word_count_mode.value_y?.toString() || '');
      }
      
      const [blocksRes, writersRes, templatesRes] = await Promise.all([
        blockAPI.getAll(articleId),
        workspaceAPI.getWriters(articleRes.data.workspace_id),
        templateAPI.getAll(articleRes.data.workspace_id)
      ]);
      
      setBlocks(blocksRes.data);
      setWriters(writersRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load article');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [articleId, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Keyword handling
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeywords();
    }
  };

  const addKeywords = () => {
    if (!keywordInput.trim()) return;
    const newKeywords = keywordInput.split(',').map(k => k.trim()).filter(k => k && !briefForm.secondary_keywords.includes(k));
    if (newKeywords.length > 0) {
      setBriefForm({ ...briefForm, secondary_keywords: [...briefForm.secondary_keywords, ...newKeywords] });
    }
    setKeywordInput('');
  };

  const removeKeyword = (keyword) => {
    setBriefForm({ ...briefForm, secondary_keywords: briefForm.secondary_keywords.filter(k => k !== keyword) });
  };

  // Save brief
  const saveBrief = async () => {
    setSaving(true);
    try {
      let wordCountModeObj = null;
      if (wordCountMode && wordCountX) {
        wordCountModeObj = {
          mode: wordCountMode,
          value_x: parseInt(wordCountX, 10),
          value_y: wordCountMode === 'range' && wordCountY ? parseInt(wordCountY, 10) : null
        };
      }
      
      const res = await articleAPI.update(articleId, {
        seo_brief: { ...briefForm, word_count_mode: wordCountModeObj }
      });
      
      setArticle(res.data);
      setEditingBrief(false);
      toast.success('Brief saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save brief');
    } finally {
      setSaving(false);
    }
  };

  // Import template
  const [templateImportCounter, setTemplateImportCounter] = useState(0);

  const importTemplate = (templateId, field) => {
    const template = templates.find(t => t.template_id === templateId);
    if (template) {
      setBriefForm(prev => ({ ...prev, [field]: template.content }));
      setTemplateImportCounter(c => c + 1);
      toast.success('Template imported');
    }
  };

  // Status change
  const handleStatusChange = async (newStatus) => {
    try {
      const res = await articleAPI.update(articleId, { status: newStatus });
      setArticle(res.data);
      toast.success(`Status changed to ${STATUS_CONFIG[newStatus]?.label}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    }
  };

  // Writer assignment
  const handleWriterAssign = async (writerId) => {
    try {
      const res = await articleAPI.update(articleId, { assigned_writer_id: writerId === 'none' ? null : writerId });
      setArticle(res.data);
      toast.success('Writer assigned');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign writer');
    }
  };

  // Block operations
  const handleAddBlock = async (blockType, headingLevel = 2, insertPosition = null) => {
    try {
      await blockAPI.create(articleId, {
        block_type: blockType,
        content: '',
        heading_level: blockType === 'heading' ? headingLevel : null,
        is_locked: blockType === 'heading' || blockType === 'reference' || blockType === 'meta_description',
        position: insertPosition
      });
      
      const blocksRes = await blockAPI.getAll(articleId);
      setBlocks(blocksRes.data);
      
      const articleRes = await articleAPI.get(articleId);
      setArticle(articleRes.data);
      
      setShowBlockMenu(false);
      toast.success('Block added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add block');
    }
  };

  const handleUpdateBlock = async (blockId, content) => {
    try {
      await blockAPI.update(articleId, blockId, { content });
      
      // Only refresh article-level data (word counts), NOT blocks
      const articleRes = await articleAPI.get(articleId);
      setArticle(articleRes.data);
    } catch (error) {
      // Silently ignore "block is locked" errors - don't spam user with toasts
      const detail = error.response?.data?.detail || '';
      if (detail.toLowerCase().includes('locked') || detail.toLowerCase().includes('cannot edit')) {
        return;
      }
      toast.error(detail || 'Failed to update block');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await blockAPI.delete(articleId, blockId);
      setBlocks(blocks.filter(b => b.block_id !== blockId));
      
      const articleRes = await articleAPI.get(articleId);
      setArticle(articleRes.data);
      toast.success('Block deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete block');
    }
  };

  const handleToggleLock = async (blockId, isLocked) => {
    try {
      await blockAPI.update(articleId, blockId, { is_locked: !isLocked });
      setBlocks(blocks.map(b => b.block_id === blockId ? { ...b, is_locked: !isLocked } : b));
    } catch (error) {
      toast.error('Failed to toggle lock');
    }
  };

  // Drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = blocks.findIndex(b => b.block_id === active.id);
    const newIndex = blocks.findIndex(b => b.block_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newBlocks = arrayMove(blocks, oldIndex, newIndex);
    setBlocks(newBlocks);
    
    try {
      await blockAPI.reorder(articleId, newBlocks.map(b => b.block_id));
    } catch (error) {
      setBlocks(blocks);
      toast.error('Failed to reorder blocks');
    }
  };

  // Block comments
  const handleAddComment = async (blockId, content) => {
    try {
      await blockAPI.addComment(articleId, blockId, { content });
      const blocksRes = await blockAPI.getAll(articleId);
      setBlocks(blocksRes.data);
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleResolveComment = async (blockId, commentId) => {
    try {
      await blockAPI.updateComment(articleId, blockId, commentId, { resolved: true });
      const blocksRes = await blockAPI.getAll(articleId);
      setBlocks(blocksRes.data);
    } catch (error) {
      toast.error('Failed to resolve comment');
    }
  };

  const handleDeleteComment = async (blockId, commentId) => {
    try {
      await blockAPI.deleteComment(articleId, blockId, commentId);
      const blocksRes = await blockAPI.getAll(articleId);
      setBlocks(blocksRes.data);
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  // Export
  const handleExport = async () => {
    try {
      const res = await articleAPI.export(articleId);
      await navigator.clipboard.writeText(res.data.html);
      toast.success('HTML copied to clipboard');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  // Preview
  const handlePreview = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/preview/${articleId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Calculate target word count (use higher number)
  const getTargetWordCount = () => {
    const wc = article?.seo_brief?.word_count_mode;
    if (!wc) return 0;
    if (wc.mode === 'range') return Math.max(wc.value_x || 0, wc.value_y || 0);
    return wc.value_x || 0;
  };

  const targetWordCount = getTargetWordCount();
  const writerWords = article?.writer_word_count || 0;
  const totalWords = article?.total_word_count || 0;
  const progressPercent = targetWordCount > 0 ? Math.min((writerWords / targetWordCount) * 100, 100) : 0;
  const hasMetaBlock = blocks.some(b => b.block_type === 'meta_description');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-lg">{article?.title}</h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[article?.status]?.color}`}>
                    {STATUS_CONFIG[article?.status]?.label}
                  </span>
                  {article?.revision_count > 0 && (
                    <span className="text-xs">Revision #{article.revision_count}</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Word Count */}
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">{writerWords}</span>
                {targetWordCount > 0 && <span> / {targetWordCount}</span>}
                <span className="ml-1">words</span>
              </div>
              
              {/* Status */}
              {article?.allowed_statuses?.length > 0 && (
                <Select value={article?.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Always show current status */}
                    {article?.status && STATUS_CONFIG[article.status] && (
                      <SelectItem value={article.status}>{STATUS_CONFIG[article.status].label}</SelectItem>
                    )}
                    {/* Show allowed transitions */}
                    {(article?.allowed_statuses || [])
                      .filter(s => s !== article?.status && STATUS_CONFIG[s])
                      .map(status => (
                        <SelectItem key={status} value={status}>{STATUS_CONFIG[status].label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Preview */}
              <Button variant="outline" size="sm" onClick={handlePreview}>
                <Eye className="w-4 h-4 mr-1" />
                Preview
              </Button>
              
              {/* Export */}
              <Button variant="outline" size="sm" onClick={handleExport}>
                <FileDown className="w-4 h-4 mr-1" />
                Export
              </Button>
              
              <NotificationBell />
            </div>
          </div>
          
          {targetWordCount > 0 && (
            <div className="mt-3">
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* SEO Brief */}
        <Collapsible open={briefOpen} onOpenChange={setBriefOpen} className="mb-6">
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <div className="w-full px-4 py-3 flex items-center justify-between">
              <CollapsibleTrigger className="flex items-center gap-2 hover:text-primary cursor-pointer flex-1">
                <span className="font-medium">SEO Brief</span>
                {briefOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </CollapsibleTrigger>
              {!editingBrief && canEditBrief && (
                <Button variant="outline" size="sm" onClick={() => { setBriefOpen(true); setEditingBrief(true); }}>
                  <Pencil className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            <CollapsibleContent>
              <div className="px-4 pb-4 border-t border-border pt-4">
                {editingBrief ? (
                  <SEOBriefForm
                    briefForm={briefForm} setBriefForm={setBriefForm}
                    keywordInput={keywordInput} setKeywordInput={setKeywordInput}
                    handleKeywordKeyDown={handleKeywordKeyDown} addKeywords={addKeywords} removeKeyword={removeKeyword}
                    wordCountMode={wordCountMode} setWordCountMode={setWordCountMode}
                    wordCountX={wordCountX} setWordCountX={setWordCountX}
                    wordCountY={wordCountY} setWordCountY={setWordCountY}
                    templates={templates} importTemplate={importTemplate}
                    onSave={saveBrief} onCancel={() => setEditingBrief(false)} saving={saving}
                    templateImportCounter={templateImportCounter}
                  />
                ) : (
                  <SEOBriefView brief={article?.seo_brief} />
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Word Count Summary */}
        <div className="bg-white rounded-lg border border-border p-4 mb-6">
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="text-muted-foreground">Writer's Word Count: </span>
              <span className="font-semibold">{writerWords}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Total Word Count: </span>
              <span className="font-semibold">{totalWords}</span>
            </div>
            {canEditBrief && (
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Assigned Writer:</Label>
                <Select value={article?.assigned_writer_id || 'none'} onValueChange={handleWriterAssign}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Assign writer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {writers.map(w => <SelectItem key={w.user_id} value={w.user_id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Content Blocks */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={blocks.map(b => b.block_id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {/* Insert at top */}
              {canAddBlocks && blocks.length > 0 && (
                <InsertBlockButton position={0} onInsert={handleAddBlock} canEditBrief={canEditBrief} hasMetaBlock={hasMetaBlock} />
              )}
              
              {blocks.map((block, index) => (
                <div key={block.block_id}>
                  <SortableContentBlock
                    block={block}
                    userPermissions={userPermissions}
                    articleStatus={article?.status}
                    onUpdate={handleUpdateBlock}
                    onDelete={handleDeleteBlock}
                    onToggleLock={handleToggleLock}
                    onAddComment={handleAddComment}
                    onResolveComment={handleResolveComment}
                    onDeleteComment={handleDeleteComment}
                    canEditBrief={canEditBrief}
                    canEditContent={canEditContent}
                    isWriterEditable={isWriterEditable}
                    user={user}
                  />
                  
                  {canAddBlocks && (
                    <InsertBlockButton position={block.position + 1} onInsert={handleAddBlock} canEditBrief={canEditBrief} hasMetaBlock={hasMetaBlock} />
                  )}
                </div>
              ))}

              {/* Empty state */}
              {blocks.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg border border-dashed border-border">
                  <Type className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">No content blocks yet</p>
                  {canAddBlocks && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button>
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Block
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-48">
                        <DropdownMenuItem onClick={() => handleAddBlock('paragraph')}>
                          <Type className="w-4 h-4 mr-2" />Paragraph
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleAddBlock('quote')}>
                          <Quote className="w-4 h-4 mr-2" />Quote
                        </DropdownMenuItem>
                        {canEditBrief && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleAddBlock('meta_description')}>
                              <FileText className="w-4 h-4 mr-2" />Meta Description
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAddBlock('reference')}>
                              <MessageSquare className="w-4 h-4 mr-2" />Reference Note
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {[1,2,3,4,5,6].map(level => (
                              <DropdownMenuItem key={level} onClick={() => handleAddBlock('heading', level)}>
                                <Heading2 className="w-4 h-4 mr-2" />H{level} Heading
                              </DropdownMenuItem>
                            ))}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>

        {/* Add Block at End */}
        {canAddBlocks && blocks.length > 0 && (
          <div className="mt-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full border-dashed">
                  <Plus className="w-4 h-4 mr-2" />Add Block
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem onClick={() => handleAddBlock('paragraph')}>
                  <Type className="w-4 h-4 mr-2" />Paragraph
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAddBlock('quote')}>
                  <Quote className="w-4 h-4 mr-2" />Quote
                </DropdownMenuItem>
                {canEditBrief && (
                  <>
                    <DropdownMenuSeparator />
                    {!hasMetaBlock && (
                      <DropdownMenuItem onClick={() => handleAddBlock('meta_description')}>
                        <FileText className="w-4 h-4 mr-2" />Meta Description
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => handleAddBlock('reference')}>
                      <MessageSquare className="w-4 h-4 mr-2" />Reference Note
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {[1,2,3,4,5,6].map(level => (
                      <DropdownMenuItem key={level} onClick={() => handleAddBlock('heading', level)}>
                        <Heading2 className="w-4 h-4 mr-2" />H{level} Heading
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </main>
    </div>
  );
}

// SEO Brief Form
function SEOBriefForm({ briefForm, setBriefForm, keywordInput, setKeywordInput, handleKeywordKeyDown, addKeywords, removeKeyword,
  wordCountMode, setWordCountMode, wordCountX, setWordCountX, wordCountY, setWordCountY, templates, importTemplate, onSave, onCancel, saving, templateImportCounter }) {
  
  const seoTemplates = templates.filter(t => t.target_field === 'seo_instructions');
  const writingTemplates = templates.filter(t => t.target_field === 'writing_guidelines');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>URL Slug</Label>
          <Input value={briefForm.url_slug} onChange={(e) => setBriefForm({ ...briefForm, url_slug: e.target.value })} placeholder="article-url-slug" className="mt-1" />
        </div>
        <div>
          <Label>Primary Keyword</Label>
          <Input value={briefForm.primary_keyword} onChange={(e) => setBriefForm({ ...briefForm, primary_keyword: e.target.value })} placeholder="main keyword" className="mt-1" />
        </div>
      </div>

      <div>
        <Label>Secondary Keywords</Label>
        <div className="mt-1 flex gap-2">
          <Input value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} onKeyDown={handleKeywordKeyDown} placeholder="keyword1, keyword2, keyword3" className="flex-1" />
          <Button type="button" variant="outline" onClick={addKeywords}>Add</Button>
        </div>
        {briefForm.secondary_keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {briefForm.secondary_keywords.map(k => (
              <span key={k} className="keyword-chip">
                {k}
                <button onClick={() => removeKeyword(k)}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label>Expected Word Count</Label>
        <div className="mt-1 flex gap-2 items-center">
          <Select value={wordCountMode} onValueChange={setWordCountMode}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Select mode" /></SelectTrigger>
            <SelectContent>
              {WORD_COUNT_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input 
            type="number" 
            value={wordCountX} 
            onChange={(e) => setWordCountX(e.target.value.replace(/[^0-9]/g, ''))} 
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => { if (e.key === '.' || e.key === '-' || e.key === 'e') e.preventDefault(); }}
            placeholder="X" 
            className="w-24" 
            min="0"
            step="1"
          />
          {wordCountMode === 'range' && (
            <>
              <span className="text-muted-foreground">to</span>
              <Input 
                type="number" 
                value={wordCountY} 
                onChange={(e) => setWordCountY(e.target.value.replace(/[^0-9]/g, ''))} 
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => { if (e.key === '.' || e.key === '-' || e.key === 'e') e.preventDefault(); }}
                placeholder="Y" 
                className="w-24"
                min="0"
                step="1"
              />
            </>
          )}
        </div>
      </div>

      <div>
        <Label>Objective for Writer</Label>
        <Textarea value={briefForm.objective} onChange={(e) => setBriefForm({ ...briefForm, objective: e.target.value })} placeholder="What is the objective?" className="mt-1" rows={3} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>SEO Guidelines</Label>
          {seoTemplates.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 text-xs">Import Template</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {seoTemplates.map(t => <DropdownMenuItem key={t.template_id} onClick={() => importTemplate(t.template_id, 'seo_instructions')}>{t.name}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="border border-input rounded-md">
          <BriefFieldEditor key={`seo-${templateImportCounter}`} content={briefForm.seo_instructions} onChange={(c) => setBriefForm(prev => ({ ...prev, seo_instructions: c }))} placeholder="SEO instructions..." />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>Writing Guidelines</Label>
          {writingTemplates.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 text-xs">Import Template</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {writingTemplates.map(t => <DropdownMenuItem key={t.template_id} onClick={() => importTemplate(t.template_id, 'writing_guidelines')}>{t.name}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="border border-input rounded-md">
          <BriefFieldEditor key={`wg-${templateImportCounter}`} content={briefForm.writing_guidelines} onChange={(c) => setBriefForm(prev => ({ ...prev, writing_guidelines: c }))} placeholder="Writing guidelines..." />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={onSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Save Brief
        </Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// SEO Brief View
function SEOBriefView({ brief }) {
  if (!brief) return <p className="text-muted-foreground text-sm">No brief data</p>;

  const wordCountLabel = () => {
    if (!brief.word_count_mode) return 'Not set';
    const { mode, value_x, value_y } = brief.word_count_mode;
    switch (mode) {
      case 'up_to': return `Up to ${value_x} words`;
      case 'greater_than': return `Greater than ${value_x} words`;
      case 'less_than': return `Less than ${value_x} words`;
      case 'range': return `${value_x} to ${value_y} words`;
      default: return `${value_x} words`;
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div><span className="text-muted-foreground">URL Slug:</span><p className="font-medium">{brief.url_slug || '—'}</p></div>
      <div><span className="text-muted-foreground">Primary Keyword:</span><p className="font-medium">{brief.primary_keyword || '—'}</p></div>
      <div><span className="text-muted-foreground">Expected Words:</span><p className="font-medium">{wordCountLabel()}</p></div>
      <div>
        <span className="text-muted-foreground">Secondary Keywords:</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {brief.secondary_keywords?.length > 0 ? brief.secondary_keywords.map(k => <span key={k} className="keyword-chip">{k}</span>) : '—'}
        </div>
      </div>
      <div className="col-span-2"><span className="text-muted-foreground">Objective:</span><p className="mt-1">{brief.objective || '—'}</p></div>
      <div className="col-span-2"><span className="text-muted-foreground">SEO Guidelines:</span><div className="mt-1" dangerouslySetInnerHTML={{ __html: brief.seo_instructions || '—' }} /></div>
      <div className="col-span-2"><span className="text-muted-foreground">Writing Guidelines:</span><div className="mt-1" dangerouslySetInnerHTML={{ __html: brief.writing_guidelines || '—' }} /></div>
    </div>
  );
}

// Insert Block Button
function InsertBlockButton({ position, onInsert, canEditBrief, hasMetaBlock }) {
  return (
    <div className="relative h-5 group flex items-center justify-center">
      <div className="absolute inset-x-0 h-px bg-transparent group-hover:bg-border transition-colors" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-border hover:bg-muted z-10">
            <Plus className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48">
          <DropdownMenuItem onClick={() => onInsert('paragraph', 2, position)}>
            <Type className="w-4 h-4 mr-2" />Paragraph
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onInsert('quote', 2, position)}>
            <Quote className="w-4 h-4 mr-2" />Quote
          </DropdownMenuItem>
          {canEditBrief && (
            <>
              <DropdownMenuSeparator />
              {!hasMetaBlock && (
                <DropdownMenuItem onClick={() => onInsert('meta_description', 2, position)}>
                  <FileText className="w-4 h-4 mr-2" />Meta Description
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onInsert('reference', 2, position)}>
                <MessageSquare className="w-4 h-4 mr-2" />Reference Note
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {[1,2,3,4,5,6].map(level => (
                <DropdownMenuItem key={level} onClick={() => onInsert('heading', level, position)}>
                  <Heading2 className="w-4 h-4 mr-2" />H{level} Heading
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Sortable wrapper for drag-and-drop
function SortableContentBlock(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.block.block_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  
  return (
    <div ref={setNodeRef} style={style}>
      <ContentBlock {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

// Content Block Component
function ContentBlock({ block, userPermissions, articleStatus, onUpdate, onDelete, onToggleLock, onAddComment, onResolveComment, onDeleteComment, canEditBrief, canEditContent, isWriterEditable, user, dragHandleProps }) {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [localWordCount, setLocalWordCount] = useState(block.word_count || 0);
  const [localCharCount, setLocalCharCount] = useState(block.char_count || 0);
  const saveTimeoutRef = useRef(null);
  const latestContentRef = useRef(block.content);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  
  const hasFullAccess = userPermissions.includes('full_access') || userPermissions.includes('*');
  const isReference = block.block_type === 'reference';
  const isHeading = block.block_type === 'heading';
  const isMeta = block.block_type === 'meta_description';

  // Local word count helper
  const countWordsLocal = (html) => {
    const text = html?.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').trim() || '';
    return text ? text.split(/\s+/).filter(w => w.length > 0).length : 0;
  };

  // Local character count helper (strips HTML tags)
  const countCharsLocal = (html) => {
    const text = html?.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ') || '';
    return text.length;
  };

  // Debounced auto-save on content change + real-time word/char count
  const handleContentChange = useCallback((newContent) => {
    latestContentRef.current = newContent;
    setLocalWordCount(countWordsLocal(newContent));
    if (isMeta) setLocalCharCount(countCharsLocal(newContent));
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      onUpdateRef.current(block.block_id, latestContentRef.current);
    }, 800);
  }, [block.block_id, isMeta]);

  // Sync counts from server when block prop updates
  useEffect(() => {
    setLocalWordCount(block.word_count || 0);
    if (isMeta) setLocalCharCount(block.char_count || 0);
  }, [block.word_count, block.char_count, isMeta]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, []);

  const canEdit = () => {
    if (articleStatus === 'finalized') return false;
    if (hasFullAccess) return true;
    if (isReference) return canEditBrief;
    // Review-level can edit content blocks in review status
    const isReviewer = userPermissions.includes('comment') || userPermissions.includes('request_revision') || userPermissions.includes('approve_content');
    if (isReviewer && articleStatus === 'ready_for_review') return true;
    if (block.is_locked && !isHeading && !isMeta) return canEditBrief;
    if (block.is_locked && (isHeading || isMeta)) return canEditBrief;
    return canEditContent && isWriterEditable;
  };

  const handleSubmitComment = () => {
    if (!newCommentText.trim()) return;
    onAddComment(block.block_id, newCommentText);
    setNewCommentText('');
    setShowCommentInput(false);
  };

  const handleUpdateComment = (commentId) => {
    if (!editingCommentText.trim()) return;
    // Note: Would need to add update content functionality
    setEditingCommentId(null);
  };

  const unresolvedComments = (block.comments || []).filter(c => !c.resolved);
  const resolvedComments = (block.comments || []).filter(c => c.resolved);

  return (
    <div className={`content-block group relative rounded-lg border border-border bg-white overflow-hidden ${isReference ? 'bg-slate-50 border-l-4 border-l-slate-400' : ''}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b border-border/50 ${isReference ? 'bg-slate-100' : 'bg-muted/30'}`}>
        <div className="flex items-center gap-2">
          {/* Drag handle */}
          <button
            className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-muted"
            {...dragHandleProps}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </button>
          
          <span className="text-xs font-medium text-muted-foreground uppercase">
            {isMeta ? 'Meta Description' : isHeading ? `H${block.heading_level}` : block.block_type}
          </span>
          
          {block.is_locked && <Lock className="w-3 h-3 text-amber-500" />}
          {isReference && <span className="text-xs italic text-slate-500">Not exported</span>}
        </div>
        
        <div className="flex items-center gap-1">
          {/* Word/Char count for meta */}
          {isMeta && (
            <span className="text-xs text-muted-foreground mr-2">
              {localWordCount} words · {localCharCount} chars
            </span>
          )}
          
          {/* Word count */}
          {!isMeta && <span className="text-xs text-muted-foreground mr-2">{localWordCount} words</span>}
          
          {/* Lock toggle */}
          {(hasFullAccess || canEditBrief) && !isReference && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onToggleLock(block.block_id, block.is_locked)}>
              {block.is_locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            </Button>
          )}
          
          {/* Comment button */}
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowCommentInput(!showCommentInput)}>
            <Plus className="w-3 h-3" />
          </Button>
          
          {/* Delete */}
          {(hasFullAccess || block.created_by_user === user?.user_id) && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => onDelete(block.block_id)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={`${isReference ? 'p-3 bg-slate-50' : 'p-0'} ${isHeading ? 'heading-block' : ''}`}>
        <RichTextEditor
          content={block.content}
          onChange={handleContentChange}
          editable={canEdit()}
          placeholder={isHeading ? 'Enter heading...' : isMeta ? 'Enter meta description...' : 'Start writing...'}
          className={isReference ? 'bg-transparent border-0' : ''}
          isHeading={isHeading}
          headingLevel={block.heading_level}
        />
      </div>

      {/* Comments Section */}
      {(showCommentInput || unresolvedComments.length > 0) && (
        <div className="border-t border-border bg-amber-50/50 p-3">
          {/* New Comment Input */}
          {showCommentInput && (
            <div className="mb-3">
              <Textarea
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                placeholder="Add a comment..."
                className="text-sm mb-2"
                rows={2}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSubmitComment}>Post</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowCommentInput(false)}>Cancel</Button>
              </div>
            </div>
          )}
          
          {/* Comments List */}
          {unresolvedComments.map(comment => (
            <div key={comment.comment_id} className="text-sm border-l-2 border-amber-400 pl-3 py-2 mb-2 last:mb-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-xs">{comment.created_by_name}</span>
                  <span className="text-xs text-muted-foreground">{comment.date} · {comment.time}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => { setEditingCommentId(comment.comment_id); setEditingCommentText(comment.content); }}>
                    Edit
                  </button>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onResolveComment(block.block_id, comment.comment_id)}>
                    <Check className="w-3 h-3 text-green-600" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onDeleteComment(block.block_id, comment.comment_id)}>
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </div>
              <p className="text-sm">{comment.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
